import { fadeUp, motion, stagger } from '@/lib/motion';
import { ArrowRight, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Seo } from '@/lib/seo';

import { BLOG_POSTS } from './posts';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function BlogIndexPage(): JSX.Element {
  return (
    <>
      <Seo
        title="Blog — AI Receptionist Guides, Tips & Industry Insights"
        description="Guides, comparisons, and practical advice on AI receptionists, virtual receptionists, and never missing a business call."
        path="/blog"
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-0 h-[400px] w-[800px] -translate-x-1/2 -translate-y-1/3 rounded-full bg-gradient-to-br from-[var(--brand-accent)]/15 via-[var(--brand-accent)]/5 to-transparent blur-3xl" />
        </div>
        <div className="container py-10 sm:py-14">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="mx-auto max-w-2xl text-center"
          >
            <motion.span
              variants={fadeUp}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-accent)]/10 px-3 py-1 text-xs font-semibold text-[var(--brand-accent)]"
            >
              Blog
            </motion.span>
            <motion.h1
              variants={fadeUp}
              className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl"
            >
              Guides & insights
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="mt-4 text-lg text-muted-foreground"
            >
              Practical advice on AI receptionists, missing fewer calls, and growing your business.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Post list */}
      <section className="py-12 sm:py-16">
        <div className="container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="mx-auto grid max-w-4xl gap-8"
          >
            {BLOG_POSTS.map((post) => (
              <motion.article
                key={post.slug}
                variants={fadeUp}
                className="group rounded-2xl border border-border bg-background p-7 transition-shadow hover:shadow-md"
              >
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="rounded-full bg-[var(--brand-accent)]/10 px-2.5 py-0.5 font-medium text-[var(--brand-accent)]">
                    {post.category}
                  </span>
                  <span>{formatDate(post.date)}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {post.readingMinutes} min read
                  </span>
                </div>
                <h2 className="mt-3 text-xl font-bold tracking-tight group-hover:text-[var(--brand-accent)] transition-colors">
                  <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">{post.description}</p>
                <Link
                  to={`/blog/${post.slug}`}
                  className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[var(--brand-accent)] hover:underline"
                >
                  Read article <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
}
